import React from "react";

import { Modal } from "@perfectserve/library";

import { Persona, UserConfiguration } from "../utils/userConfigurationMapper";

export interface UserRole {
  facilityName: string;
  roleName: string;
  facilityGuid: string;
  roleId: string;
  messagingKey?: {
    System: string;
    Id: string;
  };
  scopes: string[];
  isPractitioner?: boolean;
  displayName?: string;
  specialtyName?: string | undefined;
  practiceName?: string | undefined;
}

export interface UserSelectorProps {
  roles: UserRole[];
  selectedRole: UserRole | null;
  onRoleChange: (role: UserRole) => void;
  isOpen: boolean;
}

export const UserSelector = ({
  roles: _roles,
  selectedRole: _selectedRole,
  onRoleChange,
  isOpen,
}: UserSelectorProps) => {
  // Always use UserConfiguration - but add comprehensive safety checks
  const userConfiguration = (window as any).PSX_USER_CONFIGURATION as UserConfiguration | undefined;

  // Dummy onClose function since selection is required
  const handleClose = () => {
    // Do nothing - selection is required
  };

  // If userConfiguration is not available, show a loading state
  if (!userConfiguration || !userConfiguration.availablePersonas || userConfiguration.availablePersonas.length === 0) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="What location are you working at today?" maxWidth="500px">
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <p>Loading your available locations...</p>
          <div>⏳</div>
        </div>
      </Modal>
    );
  }

  const availablePersonas = userConfiguration.availablePersonas;
  const practitionerPersonas = availablePersonas.filter((p: Persona) => p.properties.key === "Practitioner");
  const careTeamPersonas = availablePersonas.filter((p: Persona) => p.properties.key === "CareTeamMember");

  const handleRoleSelect = (role: UserRole) => {
    onRoleChange(role);
  };

  const handlePersonaSelect = (persona: Persona) => {
    // Convert Persona back to UserRole format for compatibility
    const _isCareTeamMember = persona.properties.key === "CareTeamMember";

    const baseRole: Omit<UserRole, "specialtyName" | "practiceName"> = {
      facilityName:
        persona.properties.key === "CareTeamMember"
          ? persona.properties.value.FacilityName || "Unknown Facility"
          : persona.properties.value.PracticeName || "Unknown Practice",
      roleName:
        persona.properties.key === "CareTeamMember"
          ? persona.properties.value.RoleName || "Care Team Member"
          : "Practitioner",
      facilityGuid:
        persona.properties.key === "CareTeamMember"
          ? persona.properties.value.FacilityGuid || ""
          : persona.properties.value.PracticeId || "",
      roleId:
        persona.properties.key === "CareTeamMember"
          ? persona.properties.value.RoleId || ""
          : persona.properties.value.SpecialtyId || "",
      messagingKey: persona.messagingKey,
      scopes: persona.scopes,
      isPractitioner: persona.properties.key === "Practitioner",
      displayName: persona.displayName,
    };

    const role: UserRole = {
      ...baseRole,
      ...(persona.properties.key === "Practitioner" && persona.properties.value.SpecialtyName
        ? { specialtyName: persona.properties.value.SpecialtyName }
        : {}),
      ...(persona.properties.key === "Practitioner" && persona.properties.value.PracticeName
        ? { practiceName: persona.properties.value.PracticeName }
        : {}),
    };

    handleRoleSelect(role);
  };

  const sectionStyle: React.CSSProperties = {
    marginBottom: "1.5rem",
    padding: "0.5rem 1rem",
  };

  const headerStyle: React.CSSProperties = {
    margin: "0 0 1rem 0",
    fontSize: "1.1rem",
    fontWeight: "600",
    color: "#324851",
  };

  const optionsStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "0.75rem",
  };

  const buttonStyle: React.CSSProperties = {
    width: "100%",
    textAlign: "left",
    padding: "1rem",
    border: "1px solid #e5e7eb",
    borderRadius: "4px",
    cursor: "pointer",
  };

  const buttonHoverStyle: React.CSSProperties = {
    borderColor: "#007bff",
    backgroundColor: "#f8f9fa",
  };

  const titleStyle: React.CSSProperties = {
    fontWeight: "600",
    color: "#324851",
    fontSize: "1rem",
    marginBottom: "0.25rem",
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: "0.875rem",
    color: "#6b7280",
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="What location are you working at today?" maxWidth="600px">
      {/* Practitioner personas */}
      {practitionerPersonas.length > 0 && (
        <div style={sectionStyle}>
          <h3 style={headerStyle}>Practitioner</h3>
          <div style={optionsStyle}>
            {practitionerPersonas.map((persona: Persona, index: number) => (
              <button
                type="button"
                key={`practitioner-${index}`}
                style={buttonStyle}
                onClick={() => handlePersonaSelect(persona)}
                onMouseEnter={(e) => {
                  Object.assign(e.currentTarget.style, buttonHoverStyle);
                }}
                onMouseLeave={(e) => {
                  Object.assign(e.currentTarget.style, buttonStyle);
                }}
              >
                <div>
                  <div style={titleStyle}>{persona.displayName}</div>
                  <div style={subtitleStyle}>{persona.description || "Practitioner"}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Care Team personas */}
      {careTeamPersonas.length > 0 && (
        <div style={sectionStyle}>
          <h3 style={headerStyle}>Care Team Facilities</h3>
          <div style={optionsStyle}>
            {careTeamPersonas.map((persona: Persona, index: number) => (
              <button
                type="button"
                key={`careteam-${index}`}
                style={buttonStyle}
                onClick={() => handlePersonaSelect(persona)}
                onMouseEnter={(e) => {
                  Object.assign(e.currentTarget.style, buttonHoverStyle);
                }}
                onMouseLeave={(e) => {
                  Object.assign(e.currentTarget.style, buttonStyle);
                }}
              >
                <div>
                  <div style={titleStyle}>
                    {persona.properties.key === "CareTeamMember"
                      ? persona.properties.value.FacilityName || "Unknown Facility"
                      : persona.displayName}
                  </div>
                  <div style={subtitleStyle}>
                    {persona.properties.key === "CareTeamMember"
                      ? persona.properties.value.RoleName || "Care Team Member"
                      : persona.description}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
};
